import React, {
  useCallback,
  useState,
  useEffect,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import { useRouteMatch } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import * as Sentry from '@sentry/browser';

import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../../hooks/useSessionStore';
import useToast from '../../../../hooks/useToast';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import CoachConfig from '../../../../Model/CoachConfig';

import {
  Container,
  ConfigContainer,
  Title,
  SubTitle,
  StyledTextField,
  StyledButton,
  HeaderContainer,
} from './styles';

import texts from './texts.json';

const CheckInFeedbackSettings = () => {
  const { params: { userId: coachId } } = useRouteMatch();
  const { userId: loggedInUserId } = useSessionStore();
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  const [firstCheckInFeedbackTemplate, setFirstCheckInFeedbackTemplate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [coachConfigDoc, setCoachConfigDoc] = useState(null);

  // Load coach config document on mount
  useEffect(() => {
    const loadCoachConfig = async () => {
      setIsLoading(true);
      try {
        const coachConfig = await CoachConfig.getCoachConfigByCoachId(coachId);

        if (isComponentMountedRef.current) {
          setCoachConfigDoc(coachConfig);

          if (coachConfig && coachConfig.exists) {
            setFirstCheckInFeedbackTemplate(coachConfig.firstCheckInFeedbackTemplate || '');
          }
          setIsLoading(false);
        }
      } catch (error) {
        if (isComponentMountedRef.current) {
          Sentry.captureException(error, {
            extra: {
              coachId,
              message: texts.loadError,
            },
          });
          showToast(texts.loadError, { error: true });
          setIsLoading(false);
        }
      }
    };

    loadCoachConfig();
  }, [
    coachId,
    isComponentMountedRef,
    showToast,
  ]);

  const handleSave = useCallback(async () => {
    setIsLoading(true);

    try {
      if (coachConfigDoc?.exists) {
        await coachConfigDoc.update({
          firstCheckInFeedbackTemplate,
          lastUpdatedAt: new Date(),
          lastUpdatedBy: loggedInUserId,
        });
      } else {
        // Create new coach config document
        const newCoachConfig = new CoachConfig(coachId);
        await newCoachConfig.set({
          firstCheckInFeedbackTemplate,
          lastUpdatedAt: new Date(),
          lastUpdatedBy: loggedInUserId,
        }, { merge: true });

        if (isComponentMountedRef.current) {
          setCoachConfigDoc(newCoachConfig);
        }
      }

      if (isComponentMountedRef.current) {
        showToast(texts.settingsSaved);
        setIsLoading(false);
      }
    } catch (error) {
      if (isComponentMountedRef.current) {
        Sentry.captureException(error, {
          extra: {
            coachId,
            message: texts.saveError,
          },
        });
        showToast(texts.saveError, { error: true });
        setIsLoading(false);
      }
    }
  }, [
    coachConfigDoc,
    firstCheckInFeedbackTemplate,
    coachId,
    loggedInUserId,
    isComponentMountedRef,
    showToast,
  ]);

  const handleTemplateChange = useCallback((event) => {
    setFirstCheckInFeedbackTemplate(event.target.value);
  }, []);

  return (
    <Container>
      <ConfigContainer>
        <HeaderContainer>
          <Title>{texts.title}</Title>
          <SubTitle>{texts.description}</SubTitle>
        </HeaderContainer>
        <StyledTextField
          value={firstCheckInFeedbackTemplate}
          onChange={handleTemplateChange}
          placeholder={texts.placeholder}
          multiline
          rows={10}
          variant="outlined"
        />
      </ConfigContainer>

      <StyledButton
        variant="contained"
        onClick={handleSave}
        startIcon={<SaveIcon />}
      >
        {texts.saveButton}
      </StyledButton>

      <LoadingOverlay isLoading={isLoading} />
    </Container>
  );
};

export default compose(
  observer,
)(CheckInFeedbackSettings);
